<script setup lang="ts">
import 'gitart-vue-dialog/dist/style.css'
import { GDialog } from 'gitart-vue-dialog'
import { computed } from 'vue'
import i18n from '@/lang/i18n'

// deviceIds は選択と行単位の実行で使う。categoryLabel は一括実行で使う
// force が true なら証明書を作り直すので警告を出す。skipCount と runningCount は案内用の台数
const props = withDefaults(defineProps<{
  modelValue?: boolean,
  deviceIds?: number[],
  categoryLabel?: string | null,
  force?: boolean,
  skipCount?: number,
  runningCount?: number,
}>(), {
  modelValue: false,
  deviceIds: () => [],
  categoryLabel: null,
  force: false,
  skipCount: 0,
  runningCount: 0,
})

const emit = defineEmits<{
  (event: 'update:modelValue', value: boolean): void,
  (event: 'confirm'): void,
}>()

const title = computed(() => {
  const key = props.force ? 'forceTitle' : 'title'
  return i18n.global.t(`pages.gpsTrackerProvisioning.modal.${key}`)
})

const showSkipNotice = computed(() => !props.force && props.skipCount > 0)
const showRunningWarning = computed(() => props.force && props.runningCount > 0)

function updateDialog (value: boolean) {
  emit('update:modelValue', value)
}

function closeModal () {
  emit('update:modelValue', false)
}

function confirm () {
  emit('confirm')
}
</script>

<template>
  <GDialog
    :model-value="props.modelValue"
    @update:model-value="updateDialog"
    max-width="34%"
    transition="custom-from-bottom-transition"
  >
    <div class="modal-header justify-content-center pt-3">
      <label>{{ title }}</label>
    </div>
    <template v-if="props.categoryLabel">
      <div class="row mr-3 ml-3 pl-5 pt-2 pb-1 v-theme--light">
        {{ i18n.global.t('pages.gpsTrackerProvisioning.modal.bodyCategory', { category: props.categoryLabel, count: props.deviceIds.length }) }}
      </div>
    </template>
    <template v-else>
      <div class="row mr-3 ml-3 pl-5 pt-2 v-theme--light">
        {{ i18n.global.t('pages.gpsTrackerProvisioning.modal.bodySelected', { count: props.deviceIds.length }) }}
      </div>
      <div class="mr-3 ml-3 pl-5 pt-2 pb-1 v-theme--light selected-ids">
        <span class="selected-ids-label">{{ i18n.global.t('pages.gpsTrackerProvisioning.modal.targetIds') }}</span>
        {{ props.deviceIds.map((id) => '#' + id).join(', ') }}
      </div>
    </template>
    <div v-if="showSkipNotice" class="mr-3 ml-3 pl-5 pt-2 v-theme--light skip-notice">
      {{ i18n.global.t('pages.gpsTrackerProvisioning.modal.skipNotice', { count: props.skipCount }) }}
    </div>
    <template v-if="props.force">
      <div class="mr-3 ml-3 pl-5 pt-2 v-theme--light force-warning">
        {{ i18n.global.t('pages.gpsTrackerProvisioning.modal.forceWarning') }}
      </div>
      <div v-if="showRunningWarning" class="mr-3 ml-3 pl-5 pt-1 v-theme--light force-warning">
        {{ i18n.global.t('pages.gpsTrackerProvisioning.modal.forceRunningWarning', { count: props.runningCount }) }}
      </div>
    </template>
    <div class="d-flex justify-content-end m-3">
      <v-btn class="m-1 reset-btn" @click="closeModal">
        {{ i18n.global.t('pages.gpsTrackerProvisioning.modal.cancel') }}
      </v-btn>
      <v-btn
        v-if="props.force"
        class="m-1"
        color="red-darken-2"
        @click="confirm"
      >
        {{ i18n.global.t('pages.gpsTrackerProvisioning.modal.execute') }}
      </v-btn>
      <v-btn v-else class="m-1 exec-btn" @click="confirm">
        {{ i18n.global.t('pages.gpsTrackerProvisioning.modal.execute') }}
      </v-btn>
    </div>
  </GDialog>
</template>

<style scoped>
.modal-header {
  font-weight: 600;
}
.selected-ids {
  font-size: 0.85rem;
  color: #555;
  word-break: break-all;
}
.selected-ids-label {
  font-weight: 600;
  margin-right: 4px;
}
.skip-notice {
  font-size: 0.85rem;
  color: #555;
}
.force-warning {
  font-size: 0.85rem;
  color: #c62828;
  font-weight: 600;
}
</style>
